import { useEffect, useState } from "react";
import RIcon from "../../components/RIcon";

const STEPS = [
  { label: "身份验证", icon: "ri-shield-check-line", dur: 380 },
  { label: "权限加载", icon: "ri-key-2-line", dur: 380 },
  { label: "工作台就绪", icon: "ri-layout-masonry-line", dur: 340 },
];

interface LoginLoadingProps {
  onDone: () => void;
}

export default function LoginLoading({ onDone }: LoginLoadingProps) {
  const [phase, setPhase] = useState<"in" | "steps" | "out">("in");
  const [stepIdx, setStepIdx] = useState(-1);        // which step is active
  const [doneSteps, setDoneSteps] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const [scanLine, setScanLine] = useState(false);

  useEffect(() => {
    // Phase 1 – fade in overlay
    const t0 = setTimeout(() => setPhase("steps"), 200);

    // Phase 2 – activate steps one by one
    let offset = 320;
    STEPS.forEach((step, i) => {
      const t = setTimeout(() => {
        setStepIdx(i);
        setProgress(Math.round(((i + 1) / STEPS.length) * 100));
        setTimeout(() => setDoneSteps((prev) => [...prev, i]), step.dur - 80);
      }, offset);
      offset += step.dur;
      // Store timeouts for cleanup would be complex, skip for brevity
      void t;
    });

    // Phase 3 – scan line flash
    const tScan = setTimeout(() => setScanLine(true), offset + 50);

    // Phase 4 – fade out
    const tOut = setTimeout(() => setPhase("out"), offset + 250);

    // Phase 5 – navigate
    const tNav = setTimeout(() => onDone(), offset + 600);

    return () => {
      clearTimeout(t0);
      clearTimeout(tScan);
      clearTimeout(tOut);
      clearTimeout(tNav);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #061A19 0%, #0A3A37 50%, #0D4A46 100%)",
        opacity: phase === "out" ? 0 : 1,
        transition: phase === "out" ? "opacity 0.45s ease" : phase === "in" ? "opacity 0.25s ease" : "none",
      }}
    >
      {/* Ambient orbs */}
      <div className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(14,165,160,0.18) 0%, transparent 70%)", top: "-10%", left: "-5%" }} />
      <div className="absolute w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(170,255,69,0.10) 0%, transparent 70%)", bottom: "-8%", right: "-5%" }} />

      {/* Scan line sweep */}
      {scanLine && (
        <div
          className="absolute inset-x-0 h-px pointer-events-none"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(170,255,69,0.8), transparent)",
            top: "50%",
            animation: "scanSweep 0.4s ease forwards",
          }}
        />
      )}

      {/* Rotating ring */}
      <div
        className="absolute w-72 h-72 rounded-full border border-[#0EA5A0]/20 pointer-events-none"
        style={{ animation: "spinSlow 8s linear infinite" }}
      />
      <div
        className="absolute w-96 h-96 rounded-full border border-[#0EA5A0]/10 pointer-events-none"
        style={{ animation: "spinSlow 14s linear infinite reverse" }}
      />

      {/* Content */}
      <div
        className="relative flex flex-col items-center gap-8 px-8"
        style={{
          opacity: phase === "in" ? 0 : 1,
          transform: phase === "in" ? "scale(0.94)" : "scale(1)",
          transition: "opacity 0.4s ease 0.1s, transform 0.4s ease 0.1s",
        }}
      >
        {/* Logo + ring pulse */}
        <div className="relative flex items-center justify-center">
          {/* Pulsing rings */}
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="absolute rounded-full border border-[#0EA5A0]/30 pointer-events-none"
              style={{
                width: 64 + i * 28,
                height: 64 + i * 28,
                animation: `pingRing 2.4s ease-out ${i * 0.4}s infinite`,
              }}
            />
          ))}
          {/* Logo circle */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5A0] to-[#0D9590] flex items-center justify-center relative z-10">
            <img
              src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png"
              alt="协和医疗"
              className="w-10 h-10 object-contain brightness-0 invert"
            />
          </div>
        </div>

        {/* System name */}
        <div className="text-center">
          <p className="text-white font-semibold text-lg tracking-wide">协和医疗全域AI系统</p>
          <p className="text-white/40 text-xs mt-1 tracking-widest uppercase">Medical AI Platform</p>
        </div>

        {/* Progress bar */}
        <div className="w-64">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #0EA5A0, #AAFF45)",
                transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: "0 0 10px rgba(170,255,69,0.6)",
              }}
            />
          </div>
          <p className="text-white/30 text-xs text-right mt-1 font-mono">{progress}%</p>
        </div>

        {/* Steps */}
        <div className="flex flex-col gap-3 w-64">
          {STEPS.map((step, i) => {
            const isActive = stepIdx === i;
            const isDone = doneSteps.includes(i);
            const isPending = stepIdx < i;
            return (
              <div
                key={i}
                className="flex items-center gap-3"
                style={{
                  opacity: isPending ? 0.25 : 1,
                  transform: isActive ? "translateX(4px)" : "translateX(0)",
                  transition: "opacity 0.3s, transform 0.3s",
                }}
              >
                {/* Icon bubble */}
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300"
                  style={{
                    background: isDone
                      ? "rgba(170,255,69,0.2)"
                      : isActive
                      ? "rgba(14,165,160,0.3)"
                      : "rgba(255,255,255,0.05)",
                    border: isDone
                      ? "1px solid rgba(170,255,69,0.5)"
                      : isActive
                      ? "1px solid rgba(14,165,160,0.6)"
                      : "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  {isDone ? (
                    <RIcon name="check-line" className="text-xs" style={{ color: "#AAFF45" }} />
                  ) : isActive ? (
                    <i className={`${step.icon} text-xs text-[#0EA5A0]`}
                      style={{ animation: "spinOnce 0.5s ease" }} />
                  ) : (
                    <i className={`${step.icon} text-xs text-white/20`} />
                  )}
                </div>

                {/* Label */}
                <span
                  className="text-sm transition-colors duration-300"
                  style={{
                    color: isDone ? "#AAFF45" : isActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.25)",
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {step.label}
                </span>

                {/* Spinner for active */}
                {isActive && (
                  <div className="ml-auto w-3.5 h-3.5 border border-[#0EA5A0] border-t-transparent rounded-full"
                    style={{ animation: "spinOnce 0.6s linear infinite" }} />
                )}
                {isDone && (
                  <div className="ml-auto">
                    <RIcon name="check-double-line" className="text-xs" style={{ color: "#AAFF45" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes pingRing {
          0%   { opacity: 0.6; transform: scale(1); }
          80%  { opacity: 0; transform: scale(1.5); }
          100% { opacity: 0; transform: scale(1.5); }
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes spinOnce {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes scanSweep {
          0%   { top: 0%; opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
